import { supabase } from './supabase';
import type { Team, TeamMember } from '../types/team';

export async function getCoachIdForUser(userId: string): Promise<string | null> {
  const { data } = await supabase
    .from('coaches')
    .select('id')
    .eq('user_id', userId)
    .maybeSingle();
  return data?.id || null;
}

export async function checkTeamsAccess(userId: string): Promise<boolean> {
  const { data } = await supabase
    .from('coaches')
    .select('teams_access_enabled')
    .eq('user_id', userId)
    .maybeSingle();
  return data?.teams_access_enabled === true;
}

export async function loadCoachTeams(coachId: string): Promise<Team[]> {
  const { data, error } = await supabase
    .from('teams')
    .select('*')
    .eq('coach_id', coachId)
    .order('type', { ascending: true })
    .order('name', { ascending: true });

  if (error) throw error;

  const teamIds = (data || []).map(t => t.id);
  let countMap: Record<string, number> = {};

  if (teamIds.length > 0) {
    const { data: memberCounts } = await supabase
      .from('team_members')
      .select('team_id')
      .in('team_id', teamIds);

    (memberCounts || []).forEach(m => {
      countMap[m.team_id] = (countMap[m.team_id] || 0) + 1;
    });
  }

  return (data || []).map(t => ({
    id: t.id,
    partnerId: t.partner_id || '',
    name: t.name,
    description: t.description,
    type: t.type as 'team' | 'department',
    parentId: t.parent_id,
    isActive: t.is_active,
    createdAt: new Date(t.created_at),
    updatedAt: new Date(t.updated_at),
    memberCount: countMap[t.id] || 0,
  }));
}

export async function loadCoachTeamMembers(teamId: string): Promise<TeamMember[]> {
  const { data, error } = await supabase
    .from('team_members')
    .select(`
      id,
      team_id,
      user_id,
      role,
      added_at,
      users!team_members_user_id_fkey(full_name, email)
    `)
    .eq('team_id', teamId);

  if (error) throw error;

  const userIds = (data || []).map(m => m.user_id);
  let resultUserIds: Set<string> = new Set();
  let lastDateMap: Record<string, string | null> = {};

  if (userIds.length > 0) {
    const { data: respondents } = await supabase
      .from('respondents')
      .select('user_id, last_assessment_date')
      .in('user_id', userIds);

    (respondents || []).forEach(r => {
      lastDateMap[r.user_id] = r.last_assessment_date;
    });

    const { data: results } = await supabase
      .from('results')
      .select('user_id')
      .in('user_id', userIds);

    (results || []).forEach(r => resultUserIds.add(r.user_id));
  }

  return (data || []).map(m => ({
    id: m.id,
    teamId: m.team_id,
    userId: m.user_id,
    role: m.role as 'member' | 'lead',
    addedAt: new Date(m.added_at),
    userName: (m.users as any)?.full_name || (m.users as any)?.email || 'Unknown',
    userEmail: (m.users as any)?.email || '',
    coachName: undefined,
    hasResults: resultUserIds.has(m.user_id),
    lastAssessmentDate: lastDateMap[m.user_id]
      ? new Date(lastDateMap[m.user_id]!)
      : null,
  }));
}

export async function loadCoachRespondentsForTeam(
  coachId: string,
  excludeTeamId: string
): Promise<{ userId: string; name: string; email: string }[]> {
  const { data: respondents } = await supabase
    .from('respondents')
    .select('user_id, users!respondents_user_id_fkey(full_name, email)')
    .eq('coach_id', coachId);

  if (!respondents || respondents.length === 0) return [];

  const { data: existingMembers } = await supabase
    .from('team_members')
    .select('user_id')
    .eq('team_id', excludeTeamId);

  const existingIds = new Set((existingMembers || []).map(m => m.user_id));

  return respondents
    .filter(r => !existingIds.has(r.user_id))
    .map(r => ({
      userId: r.user_id,
      name: (r.users as any)?.full_name || (r.users as any)?.email || 'Unknown',
      email: (r.users as any)?.email || '',
    }));
}
